/**
 * String validation utilities including character filtering, length validation,
 * reserved word checking, and specialized validators for names, cities,
 * usernames, and passwords.
 */

// Declare types

// Constants
const LOWERALPHA = "abcdefghijklmnopqrstuvwxyz";
const UPPERALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ALPHA = LOWERALPHA + UPPERALPHA;
const NUMERIC = "0123456789";
const ALPHANUMERIC = ALPHA + NUMERIC;

// Set on globalThis for backwards compatibility
(globalThis as Record<string, unknown>).LOWERALPHA = LOWERALPHA;
(globalThis as Record<string, unknown>).UPPERALPHA = UPPERALPHA;
(globalThis as Record<string, unknown>).ALPHA = ALPHA;
(globalThis as Record<string, unknown>).NUMERIC = NUMERIC;
(globalThis as Record<string, unknown>).ALPHANUMERIC = ALPHANUMERIC;

/**
 * Extracts characters from string that match the mask
 */
function extract(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    if (mask.indexOf(str.charAt(i)) != -1) {
      out += str.charAt(i);
    }
  }
  return out;
}

/**
 * Strips characters from string that match the mask
 */
function strip(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    if (mask.indexOf(str.charAt(i)) == -1) {
      out += str.charAt(i);
    }
  }
  return out;
}

/**
 * Interface for validation form element
 */
interface ValidationElement {
  name: string;
  message?: string;
  minLength?: number;
  maxLength?: number;
  allowedChars?: string;
  notAllowedChars?: string;
  allowedFirstChar?: string;
  notAllowedFirstChar?: string;
  removeInvalid?: boolean;
  reserved?: Record<string, boolean>;
  password?: ValidationElement | string | null;
  checkValid?: () => boolean;
  get: () => string;
  set: (val: string) => void;
  setMessage: (msg: string) => boolean;
  getSibling: (name: string) => ValidationElement;
  registerEvent: (event: string, type: string) => void;
}

/**
 * Checks length constraints on the element value
 */
function checkLength(elementObj: ValidationElement): string | null {
  const len = elementObj.get().length;
  const max = elementObj.maxLength;
  const min = elementObj.minLength;

  if (max != null && len > max) {
    return "The entry cannot be longer than " + max + " character" + (max > 1 ? "s" : "") + ".";
  }
  if (min != null && len < min) {
    return "The entry must be at least " + min + " character" + (min > 1 ? "s" : "") + " long.";
  }
  return null;
}

/**
 * Checks if the value is reserved
 */
function checkReserved(elementObj: ValidationElement): string | null {
  if (elementObj.reserved == null) return null;
  return elementObj.reserved[elementObj.get()]
    ? "The value '" + elementObj.get() + "' is reserved or already in use."
    : null;
}

/**
 * Helper function for getAllowed that checks character constraints
 */
function getAllowedAux(
  elementObj: ValidationElement,
  chars: string | undefined,
  allow: boolean,
  first: boolean | null
): string | undefined {
  let out = elementObj.get();
  if (chars == null || out <= "") return undefined;

  const remove = (elementObj.removeInvalid != false);
  let firstCh = out.charAt(0);
  let after = out.substring(1);

  if (first === true) {
    out = firstCh;
    firstCh = "";
  } else if (first === false) {
    out = after;
    after = "";
  } else {
    firstCh = after = "";
  }

  const hasUpper = chars.indexOf(UPPERALPHA) != -1;
  const hasLower = chars.indexOf(LOWERALPHA) != -1;

  if (hasUpper && !hasLower) out = allow ? out.toUpperCase() : out.toLowerCase();
  if (hasLower && !hasUpper) out = allow ? out.toLowerCase() : out.toUpperCase();

  const ex = allow ? extract(out, chars) : strip(out, chars);
  elementObj.set(firstCh + (remove && first != true ? ex : out) + after);

  if (!(remove && first != true) && ex != out) {
    return first === true
      ? "The first character may not be '" + out + "'"
      : "The entry contains invalid characters";
  }

  return undefined;
}

/**
 * Validates character constraints on the element
 */
function getAllowed(elementObj: ValidationElement): string | undefined {
  const firstReq = (elementObj.allowedFirstChar ?? '') > "" || (elementObj.notAllowedFirstChar ?? '') > "";
  const mssg1 = getAllowedAux(elementObj, elementObj.allowedChars, true, firstReq ? false : null);
  const mssg2 = getAllowedAux(elementObj, elementObj.notAllowedChars, false, firstReq ? false : null);
  const mssg3 = getAllowedAux(elementObj, elementObj.allowedFirstChar, true, true);
  const mssg4 = getAllowedAux(elementObj, elementObj.notAllowedFirstChar, false, true);
  return mssg1 != null ? mssg1 : (mssg2 != null ? mssg2 : (mssg3 != null ? mssg3 : mssg4));
}

/**
 * Validates password confirmation matches
 */
function validatePassword2(this: ValidationElement): boolean {
  if (this.password == null) return true;
  if (this.password != null && typeof this.password === "string") {
    this.password = this.getSibling(this.password);
    (this.password as ValidationElement).registerEvent("thisObj.validateSibling('" + this.name + "')", "change");
  }
  if (this.get() != (this.password as ValidationElement).get()) {
    return this.setMessage("Confirmation password must be exactly the same as password.");
  }
  if (this.get() == "") {
    return this.setMessage("Confirmation password cannot be empty.");
  }
  return true;
}

/**
 * Main string validation function
 */
function validateString(this: ValidationElement): boolean {
  if (this.minLength == null) this.minLength = 1;
  const mssg = getAllowed(this);
  if (mssg != null) return this.setMessage(mssg);
  const resMssg = checkReserved(this);
  if (resMssg != null) return this.setMessage(resMssg);
  return this.setMessage(checkLength(this) || "");
}

/**
 * Validates JSON string
 */
function validateJson(this: ValidationElement): boolean {
  try {
    JSON.parse(this.get());
    return this.setMessage("");
  } catch (e: unknown) {
    return this.setMessage("Invalid Json: " + (e as Error).message);
  }
}

/**
 * Validates string and JSON together
 */
function validateStringAndJson(this: ValidationElement): boolean {
  validateString.call(this);
  const stringMessage = this.message || "";
  validateJson.call(this);
  const jsonMessage = this.message;
  return this.setMessage(stringMessage + (stringMessage && jsonMessage ? " " : "") + jsonMessage);
}

/**
 * Validates name characters
 */
function validateNameChars(this: ValidationElement): boolean {
  if (this.allowedChars == null) this.allowedChars = ALPHA + "`-',. ";
  this.checkValid = validateString;
  return this.checkValid.call(this);
}

/**
 * Validates city characters
 */
function validateCityChars(this: ValidationElement): boolean {
  if (this.allowedChars == null) this.allowedChars = ALPHA + "`-',. /\\";
  this.checkValid = validateString;
  return this.checkValid.call(this);
}

/**
 * Validates My1Link username
 */
function validateMy1LinkUsername(this: ValidationElement): boolean {
  this.allowedChars = LOWERALPHA + NUMERIC;
  this.minLength = 2;
  this.maxLength = 12;
  this.removeInvalid = false;

  if (ALPHA.indexOf(this.get().charAt(0)) == -1) {
    return this.setMessage("Username must begin with a letter.");
  }
  if (getAllowed(this) != null) {
    return this.setMessage("Username can only contain letters and numbers.");
  }
  return this.setMessage(checkLength(this) || "");
}

/**
 * Validates My1Link password
 */
function validateMy1LinkPassword(this: ValidationElement): boolean {
  this.allowedChars = ALPHANUMERIC;
  this.minLength = 6;
  this.maxLength = 8;
  this.removeInvalid = false;

  if (ALPHA.indexOf(this.get().charAt(0)) == -1) {
    return this.setMessage("Password must begin with a letter.");
  }
  if (getAllowed(this) != null) {
    return this.setMessage("Password can only contain letters and numbers");
  }
  return this.setMessage(checkLength(this) || "");
}

// Export for module usage
export {
  validatePassword2,
  validateString,
  validateJson,
  validateStringAndJson,
  validateNameChars,
  validateCityChars,
  validateMy1LinkUsername,
  validateMy1LinkPassword,
  getAllowed,
  getAllowedAux,
  checkReserved,
  checkLength,
  extract,
  strip,
  LOWERALPHA,
  UPPERALPHA,
  ALPHA,
  NUMERIC,
  ALPHANUMERIC
};

// Expose on globalThis for backwards compatibility
(globalThis as Record<string, unknown>).validatePassword2 = validatePassword2;
(globalThis as Record<string, unknown>).validateString = validateString;
(globalThis as Record<string, unknown>).validateJson = validateJson;
(globalThis as Record<string, unknown>).validateStringAndJson = validateStringAndJson;
(globalThis as Record<string, unknown>).validateNameChars = validateNameChars;
(globalThis as Record<string, unknown>).validateCityChars = validateCityChars;
(globalThis as Record<string, unknown>).validateMy1LinkUsername = validateMy1LinkUsername;
(globalThis as Record<string, unknown>).validateMy1LinkPassword = validateMy1LinkPassword;
(globalThis as Record<string, unknown>).getAllowed = getAllowed;
(globalThis as Record<string, unknown>).getAllowedAux = getAllowedAux;
(globalThis as Record<string, unknown>).checkReserved = checkReserved;
(globalThis as Record<string, unknown>).checkLength = checkLength;

// Script resource notification
if (typeof _SR_ !== 'undefined' && _SR_ !== null) {
  _SR_.notify("validatestrings.js");
}
